import type { AdminUatData, UatFile, UatHistoryEntry, UatItem, UatNote, UatProject } from '../types'

/**
 * TEST DATA for the local UAT tab preview (Summit State, a fictional
 * customer). Nothing here is a real customer item, case, person, file or
 * Linear issue. The preview pages show a "Test Data" alert with it.
 */

const P_MEMBER = '6f3c1d2e-0000-4000-8000-0000000000p1'
const P_EXTENSION = '6f3c1d2e-0000-4000-8000-0000000000p2'
const P_REQUESTS = '6f3c1d2e-0000-4000-8000-0000000000p3'

const PREVIEW_FILE: UatFile = {
  id: '6f3c1d2e-0000-4000-8000-0000000000f9',
  title: 'Summit Career Helper Extension',
  note: 'Unzip the file. In Chrome, open chrome://extensions, turn on Developer Mode, select Load Unpacked and choose the folder.',
  fileName: 'summit-career-helper-1.0.0.zip',
  versionLabel: '1.0.0',
  byteSize: 692_259,
  updatedAt: '2026-11-04T14:00:00.000Z',
  downloadUrl: '#test-data',
}

export const UAT_PREVIEW_PROJECTS: UatProject[] = [
  { id: P_REQUESTS, name: 'Your Requests', description: 'Issues, questions and requests your team added.', targetDate: null, isRequests: true, files: [] },
  { id: P_MEMBER, name: 'Member Experience', description: 'The new student home, job board and resume tools. Test data for local review.', targetDate: '2026-11-20', isRequests: false, files: [] },
  { id: P_EXTENSION, name: 'Chrome Extension', description: 'Save jobs from any job site into the tracker. Test data for local review.', targetDate: '2026-11-24', isRequests: false, files: [PREVIEW_FILE] },
]

const status = (id: string, from: UatHistoryEntry['fromValue'], to: UatHistoryEntry['toValue'], createdAt: string, authorName = 'Prentus'): UatHistoryEntry => (
  { id, kind: 'status', authorName, authorSide: authorName === 'Prentus' ? 'prentus' : 'customer', body: null, fromValue: from, toValue: to, caseText: null, createdAt, replies: [], threaded: false }
)

export const UAT_PREVIEW_ITEMS: UatItem[] = [
  {
    id: '6f3c1d2e-0000-4000-8000-000000000001',
    projectId: P_MEMBER,
    title: 'Job Board Saved Filters',
    description: 'Students can save a set of Job Board filters and open it again from the sidebar. Test data for local review.',
    linearIdentifier: 'P-9001',
    deliveredOn: '2026-10-28',
    targetDate: null,
    status: 'in_testing',
    itemType: 'uat',
    addedByCustomer: false,
    requestType: null,
    updatedAt: '2026-11-02T15:10:00.000Z',
    files: [],
    cases: [
      { id: '6f3c1d2e-0000-4000-8000-0000000000a1', text: 'Save a filter set with location and job type, then reload the page.', area: 'Job Board', result: 'pass', note: null, resultBy: 'Test Tester', resultAt: '2026-11-02T14:40:00.000Z' },
      { id: '6f3c1d2e-0000-4000-8000-0000000000a2', text: 'Rename a saved filter set from the sidebar.', area: 'Job Board', result: 'fail', note: 'The new name shows only after a page reload.', resultBy: 'Test Tester', resultAt: '2026-11-02T15:05:00.000Z' },
      { id: '6f3c1d2e-0000-4000-8000-0000000000a3', text: 'Delete a saved filter set and confirm it is gone on another device.', area: 'Job Board', result: 'pass', note: null, resultBy: 'Test Tester', resultAt: '2026-11-02T15:07:00.000Z' },
      { id: '6f3c1d2e-0000-4000-8000-0000000000a4', text: 'Open a saved filter set on a 375px phone screen.\nExpected: The filters fit without a sideways scroll.', area: 'Mobile', result: 'not_run', note: null, resultBy: null, resultAt: null },
      { id: '6f3c1d2e-0000-4000-8000-0000000000a5', text: 'Share a saved filter set link with another student.', area: 'Job Board', result: 'not_run', note: null, resultBy: null, resultAt: null },
    ],
    history: [
      status('6f3c1d2e-0000-4000-8000-0000000000b1', null, 'shipped', '2026-10-28T13:00:00.000Z'),
      status('6f3c1d2e-0000-4000-8000-0000000000b2', 'shipped', 'in_testing', '2026-11-02T14:30:00.000Z', 'Test Tester'),
      { id: '6f3c1d2e-0000-4000-8000-0000000000b3', kind: 'case_result', authorName: 'Test Tester', authorSide: 'customer', body: null, fromValue: 'not_run', toValue: 'pass', caseText: 'Save a filter set with location and job type, then reload the page.', createdAt: '2026-11-02T14:40:00.000Z', replies: [], threaded: false },
      { id: '6f3c1d2e-0000-4000-8000-0000000000b4', kind: 'case_result', authorName: 'Test Tester', authorSide: 'customer', body: 'The new name shows only after a page reload.', fromValue: 'not_run', toValue: 'fail', caseText: 'Rename a saved filter set from the sidebar.', createdAt: '2026-11-02T15:05:00.000Z', replies: [{ id: '6f3c1d2e-0000-4000-8000-0000000000e1', body: 'This looks like something the team should check, because the item notes say a new name shows right away without a reload. The Prentus team will follow up in this thread.', authorName: 'Prentus Assistant', authorSide: 'assistant', createdAt: '2026-11-02T15:05:40.000Z', corrected: false, sources: [{ label: 'UAT item: Job Board Saved Filters', href: '#uat' }] }, { id: '6f3c1d2e-0000-4000-8000-0000000000e2', body: 'Thanks for catching this. We reproduced it and the fix is in the next delivery.', authorName: 'Test Prentus Lead', authorSide: 'prentus', createdAt: '2026-11-02T17:20:00.000Z', corrected: false, sources: [] }], threaded: true },
    ],
  },
  {
    id: '6f3c1d2e-0000-4000-8000-000000000002',
    projectId: P_MEMBER,
    title: 'Advisor Review Queue Sorting',
    description: 'Advisors can sort the review queue by submission date or by program. Test data for local review.',
    linearIdentifier: 'P-9002',
    deliveredOn: '2026-10-21',
    targetDate: null,
    status: 'passed',
    itemType: 'uat',
    addedByCustomer: false,
    requestType: null,
    updatedAt: '2026-10-30T18:00:00.000Z',
    files: [],
    cases: [
      { id: '6f3c1d2e-0000-4000-8000-0000000000c1', text: 'Sort the queue by submission date, newest first.', area: 'Advisor Tools', result: 'pass', note: null, resultBy: 'Test Tester', resultAt: '2026-10-30T17:40:00.000Z' },
      { id: '6f3c1d2e-0000-4000-8000-0000000000c2', text: 'Sort the queue by program name.', area: 'Advisor Tools', result: 'pass', note: null, resultBy: 'Test Tester', resultAt: '2026-10-30T17:45:00.000Z' },
    ],
    history: [
      status('6f3c1d2e-0000-4000-8000-0000000000d1', null, 'shipped', '2026-10-21T13:00:00.000Z'),
      { id: '6f3c1d2e-0000-4000-8000-0000000000d2', kind: 'note', authorName: 'Test Tester', authorSide: 'customer', body: 'Sorting works on both campuses. Ready to pass.', fromValue: null, toValue: null, caseText: null, createdAt: '2026-10-30T17:55:00.000Z', replies: [], threaded: true },
      status('6f3c1d2e-0000-4000-8000-0000000000d3', 'in_testing', 'passed', '2026-10-30T18:00:00.000Z', 'Test Tester'),
    ],
  },
  {
    id: '6f3c1d2e-0000-4000-8000-000000000003',
    projectId: P_MEMBER,
    title: 'Resume Review Email Reminders',
    description: 'Students get one email when a resume review is waiting for more than three days. Test data for local review.',
    linearIdentifier: 'P-9003',
    deliveredOn: '2026-10-24',
    targetDate: null,
    status: 'needs_fix',
    itemType: 'production',
    addedByCustomer: false,
    requestType: null,
    updatedAt: '2026-10-29T16:20:00.000Z',
    files: [],
    cases: [
      { id: '6f3c1d2e-0000-4000-8000-0000000000e9', text: 'Leave a resume review open for three days and check the inbox.', area: 'Notifications', result: 'fail', note: 'Two reminder emails arrived on day three.', resultBy: 'Test Tester', resultAt: '2026-10-29T16:15:00.000Z' },
    ],
    history: [
      status('6f3c1d2e-0000-4000-8000-0000000000f1', null, 'shipped', '2026-10-24T13:00:00.000Z'),
      { id: '6f3c1d2e-0000-4000-8000-0000000000f2', kind: 'status', authorName: 'Test Tester', authorSide: 'customer', body: 'Students got two reminders instead of one.', fromValue: 'in_testing', toValue: 'needs_fix', caseText: null, createdAt: '2026-10-29T16:20:00.000Z', replies: [], threaded: true },
    ],
  },
  {
    id: '6f3c1d2e-0000-4000-8000-000000000004',
    projectId: P_MEMBER,
    title: 'Career Fair Check-In Page',
    description: 'Employers check students in at a career fair with a QR code. Test data for local review.',
    linearIdentifier: 'P-9004',
    deliveredOn: null,
    targetDate: '2026-11-18',
    status: 'planned',
    itemType: 'roadmap',
    addedByCustomer: false,
    requestType: null,
    updatedAt: '2026-11-03T13:00:00.000Z',
    files: [],
    cases: [],
    history: [status('6f3c1d2e-0000-4000-8000-000000000101', null, 'planned', '2026-11-03T13:00:00.000Z')],
  },
  {
    id: '6f3c1d2e-0000-4000-8000-000000000005',
    projectId: P_EXTENSION,
    title: 'Save a Job From Any Site',
    description: 'Students save a job posting from any job site into the tracker with one click. Test data for local review.',
    linearIdentifier: 'P-9005',
    deliveredOn: '2026-11-04',
    targetDate: null,
    status: 'shipped',
    itemType: 'uat',
    addedByCustomer: false,
    requestType: null,
    updatedAt: '2026-11-04T14:00:00.000Z',
    files: [],
    cases: [
      { id: '6f3c1d2e-0000-4000-8000-0000000000g1', text: 'Install the extension from the download above and sign in.', area: 'Extension', result: 'not_run', note: null, resultBy: null, resultAt: null },
      { id: '6f3c1d2e-0000-4000-8000-0000000000g2', text: 'Open a job posting and select Save Job.', area: 'Extension', result: 'not_run', note: null, resultBy: null, resultAt: null },
    ],
    history: [status('6f3c1d2e-0000-4000-8000-000000000102', 'planned', 'shipped', '2026-11-04T14:00:00.000Z')],
  },
  {
    id: '6f3c1d2e-0000-4000-8000-000000000006',
    projectId: P_REQUESTS,
    title: 'Export the advisor queue to a spreadsheet',
    description: 'Our advisors want to export the review queue each Friday. Test data for local review.',
    linearIdentifier: 'P-9006',
    deliveredOn: null,
    targetDate: null,
    status: 'new',
    itemType: 'roadmap',
    addedByCustomer: true,
    requestType: 'request',
    updatedAt: '2026-11-04T15:30:00.000Z',
    files: [],
    cases: [],
    history: [
      { id: '6f3c1d2e-0000-4000-8000-000000000103', kind: 'request', authorName: 'Test Tester', authorSide: 'customer', body: 'Our advisors want to export the review queue each Friday.', fromValue: null, toValue: null, caseText: null, createdAt: '2026-11-04T15:30:00.000Z', replies: [{ id: '6f3c1d2e-0000-4000-8000-0000000000e3', body: 'Thanks. We added this to our list and will reply here with a date.', authorName: 'Test Prentus Lead', authorSide: 'prentus', createdAt: '2026-11-04T16:10:00.000Z', corrected: false, sources: [] }], threaded: true },
    ],
  },
  {
    id: '6f3c1d2e-0000-4000-8000-000000000007',
    projectId: P_MEMBER,
    title: 'Some Students Cannot Open Their Saved Jobs',
    description: 'In the live site today, some students see an error when they open Saved Jobs. Test data for local review.',
    linearIdentifier: 'P-9007',
    deliveredOn: null,
    targetDate: '2026-11-06',
    status: 'planned',
    itemType: 'production',
    addedByCustomer: false,
    requestType: null,
    updatedAt: '2026-11-04T12:00:00.000Z',
    files: [],
    cases: [],
    history: [status('6f3c1d2e-0000-4000-8000-000000000104', null, 'planned', '2026-11-04T12:00:00.000Z')],
  },
  {
    id: '6f3c1d2e-0000-4000-8000-000000000008',
    projectId: P_MEMBER,
    title: 'Text Message Reminders for Events',
    description: 'Students get a text message the day before an event they joined. Test data for local review.',
    linearIdentifier: null,
    deliveredOn: null,
    targetDate: null,
    status: 'planned',
    itemType: 'roadmap',
    addedByCustomer: false,
    requestType: null,
    updatedAt: '2026-11-03T13:00:00.000Z',
    files: [],
    cases: [],
    history: [status('6f3c1d2e-0000-4000-8000-000000000105', null, 'planned', '2026-11-03T13:00:00.000Z')],
  },
]

/** TEST DATA for the local admin UAT editor preview. */
/** A labeled stand-in screenshot (an inline SVG), so the preview loads no real customer image. */
function testScreenshot(label: string): string {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="1400" height="875" viewBox="0 0 1400 875"><rect width="1400" height="875" fill="#eef5f1"/><rect x="60" y="60" width="1280" height="755" rx="24" fill="#ffffff" stroke="#c9d8d0" stroke-width="4"/><rect x="60" y="60" width="1280" height="96" rx="24" fill="#015840"/><text x="700" y="470" font-family="Arial, sans-serif" font-size="56" font-weight="700" fill="#015840" text-anchor="middle">Test Screenshot</text><text x="700" y="540" font-family="Arial, sans-serif" font-size="34" fill="#3d4a44" text-anchor="middle">${label}</text></svg>`
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`
}

/** TEST DATA: What's Changing sections for the Member Experience project (Summit State). */
export const UAT_PREVIEW_NOTES: UatNote[] = [
  {
    id: '6f3c1d2e-0000-4000-8000-0000000000n1',
    projectId: P_MEMBER,
    title: 'Job Board',
    whatMembersSee: 'The Job Board opens with one search bar and a personal feed called **Job Matches**. Test data for local review.\n- Job details open in a side panel.\n- **Mark as Applied** shows after a student opens a job link.\n  - The job is tracked first if it was not tracked yet.',
    whatChanged: '- One search bar replaces the two old ones.\n- Saved filters keep their sort order.\n- Filters take less room on a phone.',
    studentVsJobseeker: '| Area | Jobseeking | Student |\n|---|---|---|\n| Job Matches | Shown | Hidden |\n| Mark as Applied | Open | Locked |\n| Saved Filters | Open | Open |',
    schoolNotes: 'Summit State turns on the daily match email for all jobseekers. Test data for local review.',
    images: [
      { id: '6f3c1d2e-0000-4000-8000-0000000000i1', url: testScreenshot('Job Board with Job Matches'), caption: 'The Job Board with Job Matches at the top. Test data.' },
      { id: '6f3c1d2e-0000-4000-8000-0000000000i2', url: testScreenshot('Job details side panel'), caption: 'Job details open in a side panel. Test data.' },
    ],
    itemIds: ['6f3c1d2e-0000-4000-8000-000000000001'],
    updatedAt: '2026-11-03T15:00:00.000Z',
    publishedAt: '2026-11-03T15:00:00.000Z',
    comments: [
      { id: '6f3c1d2e-0000-4000-8000-0000000000n9', kind: 'section_comment', authorName: 'Test Tester', authorSide: 'customer', body: 'Do students see Job Matches before they finish their preferences?', fromValue: null, toValue: null, caseText: null, createdAt: '2026-11-04T13:20:00.000Z', replies: [{ id: '6f3c1d2e-0000-4000-8000-0000000000e9', body: 'No. Job Matches shows only for jobseekers, and a Missing Job Preferences card shows until they save preferences.', authorName: 'Prentus Assistant', authorSide: 'assistant', createdAt: '2026-11-04T13:20:40.000Z', corrected: false, sources: [{ label: "What's Changing: Job Board", href: '#uat/changes/6f3c1d2e-0000-4000-8000-0000000000n1' }] }], threaded: true },
    ],
  },
  {
    id: '6f3c1d2e-0000-4000-8000-0000000000n2',
    projectId: P_MEMBER,
    title: 'Resume',
    whatMembersSee: 'Students edit a resume on the page and select **Save**. Test data for local review.',
    whatChanged: '- Coaches comment on highlighted text.\n- A student resolves every comment before they resubmit.',
    studentVsJobseeker: '',
    schoolNotes: '',
    images: [{ id: '6f3c1d2e-0000-4000-8000-0000000000i3', url: testScreenshot('Resume editor'), caption: 'The resume editor. Test data.' }],
    itemIds: ['6f3c1d2e-0000-4000-8000-000000000003'],
    updatedAt: '2026-11-02T18:30:00.000Z',
    publishedAt: '2026-11-02T18:30:00.000Z',
    comments: [],
  },
]

export function uatPreviewAdminData(): AdminUatData {
  return {
    projects: UAT_PREVIEW_PROJECTS.map((project, position) => ({ id: project.id, key: null, name: project.name, description: project.description, targetDate: project.targetDate, linearIdentifier: null, linearIssueUrl: null, position })),
    items: UAT_PREVIEW_ITEMS.map(({ history: _history, files: _files, ...item }, position) => ({
      ...item,
      key: null,
      linearIssueUrl: item.linearIdentifier ? `https://linear.app/prentus/issue/${item.linearIdentifier}` : null,
      position,
      updatedBy: 'test-data@prentus.com',
      spec: '',
      mergedIntoId: null,
      shippedAt: item.status === 'planned' || item.status === 'new' ? null : `${item.deliveredOn || '2026-11-04'}T14:00:00.000Z`,
      linearStateName: item.status === 'planned' || item.status === 'new' ? 'In Progress' : 'Complete',
      linearCheckedAt: '2026-11-04T16:00:00.000Z',
      linearCheckError: null,
    })),
    files: [{
      id: PREVIEW_FILE.id,
      key: null,
      projectId: P_EXTENSION,
      itemId: null,
      title: PREVIEW_FILE.title,
      note: PREVIEW_FILE.note,
      visibility: 'until_shipped',
      gateType: 'next_ship',
      gateIssues: [],
      gateSetAt: '2026-11-04T14:00:00.000Z',
      gateCheckedAt: '2026-11-04T16:00:00.000Z',
      gateError: null,
      releasedAt: null,
      releaseReason: null,
      customerVisible: false,
      current: { id: '6f3c1d2e-0000-4000-8000-0000000000v1', versionLabel: '1.0.0', fileName: PREVIEW_FILE.fileName, byteSize: PREVIEW_FILE.byteSize, status: 'ready', uploadedBy: 'test-data@prentus.com', createdAt: PREVIEW_FILE.updatedAt, current: true },
      versions: [{ id: '6f3c1d2e-0000-4000-8000-0000000000v1', versionLabel: '1.0.0', fileName: PREVIEW_FILE.fileName, byteSize: PREVIEW_FILE.byteSize, status: 'ready', uploadedBy: 'test-data@prentus.com', createdAt: PREVIEW_FILE.updatedAt, current: true }],
      downloadCount: 0,
      lastDownloadAt: null,
      downloadUrl: null,
    }],
    notes: UAT_PREVIEW_NOTES.map(({ comments, images, ...note }, position) => ({
      ...note,
      key: null,
      position,
      status: 'published' as const,
      images: images.map((image) => ({ ...image, fileName: 'test-screenshot.png', byteSize: 120_000, status: 'ready' as const })),
      publishedAt: note.updatedAt,
      editedAt: note.updatedAt,
      updatedBy: 'test-data@prentus.com',
      copied: false,
      commentCount: comments.length,
    })),
    events: UAT_PREVIEW_ITEMS.flatMap((item) => item.history.map((entry) => ({
      ...entry,
      itemId: item.id,
      noteId: null,
      itemTitle: item.title,
      authorEmail: entry.authorSide === 'prentus' ? 'test-data@prentus.com' : 'tester@example.edu',
      syncStatus: entry.threaded ? 'pending' as const : 'none' as const,
      syncError: null,
      linearIssueIdentifier: null,
      linearIssueUrl: null,
      slackNotifiedAt: null,
    }))),
    linearKeyConfigured: false,
    routing: { labelCount: 0, customerLinked: false, titlePrefix: null, source: 'none' },
    email: { subscribed: 2, unsubscribed: 0, recent: [{ date: '2026-11-04', status: 'sent', itemCount: 1, fileCount: 0, recipientCount: 2, error: null }] },
  }
}
